import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  List,
  ListItem,
  TextField,
  Button,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Link,
  Box,
  InputAdornment,
  Paper,
  Tabs,
  Tab,
  Checkbox,
  CircularProgress,
  useMediaQuery,
  useTheme,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  Menu,
  MenuItem,
  Chip,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as wishlistItemsService from "../services/wishlistItemsService";
import {
  useWishlistItemsQuery,
  useAddItem,
  useUpdateItem,
  useDeleteItem,
  useClaimItem,
  useUnclaimItem,
  useBulkImportItems,
  useReorderItems,
} from "../hooks/useWishlistItems";
import ThumbnailPicker from "./ThumbnailPicker";
import ItemThumbnail from "./ItemThumbnail";
import { digitsToNumber, numberToDigits, digitsToDisplay, handleDigitsChange } from "../utils/priceMask";
import { validateAndResizeImage } from "../utils/thumbnailValidation";

const LOCKED_TOOLTIP = "Wishlist editing is closed for this group";
const REORDER_TOOLTIP = "Drag to reorder";

const THUMBNAIL_SIZE = { xs: 60, sm: 72 };

// Pure reorder math, exported so it can be unit-tested without simulating a real
// drag gesture (dnd-kit's sensors need pointer events JSDOM doesn't provide).
export function computeReorderPayload(items, activeId, overId) {
  const oldIndex = items.findIndex((i) => i.id === activeId);
  const newIndex = items.findIndex((i) => i.id === overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return null;

  const reordered = arrayMove(items, oldIndex, newIndex);
  // Only rows whose rank actually moved need writing — one move shifts a
  // contiguous span, not the whole list.
  const updates = reordered
    .map((item, index) => ({ id: item.id, sort_order: index + 1, previous: item.sort_order }))
    .filter((row) => row.previous !== row.sort_order)
    .map(({ id, sort_order }) => ({ id, sort_order }));

  return { reordered, updates };
}

// Diagonal gift-tag banner in the card's top-left corner. pointerEvents: none so it
// can never intercept a click on the drag handle beneath it.
function RankRibbon({ rank }) {
  return (
    <Box
      role="img"
      aria-label={`Rank ${rank}`}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: { xs: 60, sm: 72 },
        height: { xs: 60, sm: 72 },
        // Square corners here on purpose: the card already clips to its own rounded
        // corner, and rounding this wrapper too would eat into the band.
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      {/* Band is wider than the wrapper's diagonal and centred slightly toward the
          corner, so rotating -45deg lays it flat across it. Sized so a two-digit
          rank still reads clearly. */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 15, sm: 19 },
          left: { xs: -14, sm: -16 },
          width: { xs: 88, sm: 104 },
          transform: "rotate(-45deg)",
          textAlign: "center",
          bgcolor: "primary.main",
          color: "#fff",
          fontWeight: 700,
          fontSize: { xs: 14, sm: 15 },
          lineHeight: "24px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
        }}
      >
        {rank}
      </Box>
    </Box>
  );
}

function DragHandle({ attributes, listeners, setActivatorNodeRef, locked }) {
  return (
    <Tooltip title={locked ? LOCKED_TOOLTIP : REORDER_TOOLTIP}>
      <span>
        <IconButton
          aria-label="drag to reorder"
          disabled={locked}
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          sx={{
            color: "text.secondary",
            cursor: locked ? "not-allowed" : "grab",
            // Required, or the browser claims the touch for scrolling instead.
            touchAction: "none",
          }}
        >
          <DragIndicatorIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
}

// Renders only the card shell (ribbon + handle slot); the card body stays inline in
// the parent so it keeps direct access to the editing state and handlers there.
function SortableItemCard({ id, rank, disabled, showHandle, locked, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  return (
    <ListItem
      ref={setNodeRef}
      divider
      disableGutters
      style={{
        // Y-only translation: locks movement to the vertical axis, and CSS.Translate
        // (not CSS.Transform) avoids the scaling that distorts variable-height cards.
        transform: CSS.Translate.toString(transform ? { ...transform, x: 0 } : null),
        transition,
      }}
      sx={{
        position: "relative",
        bgcolor: "background.paper",
        borderRadius: 3,
        boxShadow: isDragging ? "0 20px 40px rgba(0,0,0,0.35)" : "0 16px 30px rgba(0,0,0,0.06)",
        p: { xs: 1.5, sm: 2.5 },
        overflow: "hidden",
        zIndex: isDragging ? 1 : "auto",
        opacity: isDragging ? 0.9 : 1,
      }}
    >
      <RankRibbon rank={rank} />
      <Stack direction="row" alignItems="stretch" sx={{ width: "100%", minWidth: 0 }}>
        {/* Slot is reserved whether or not a handle renders, so the ribbon never
            overlaps the thumbnail and owner/non-owner cards line up identically. */}
        <Box
          sx={{
            width: { xs: 40, sm: 46 },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {showHandle && (
            <DragHandle
              attributes={attributes}
              listeners={listeners}
              setActivatorNodeRef={setActivatorNodeRef}
              locked={locked}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Stack>
    </ListItem>
  );
}

export default function WishlistItems({ userId, nameId, isOwner, isChildList = false, isParent = false, isWishlistLocked = false, childLists = [] }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const hideClaimButton = isOwner && !isChildList;
  const showChildSelector = isParent && !isOwner && childLists.length > 0;

  const { data: items = [], reorderPendingRef } = useWishlistItemsQuery(nameId, isOwner);
  const addItemMutation = useAddItem(nameId);
  const updateItemMutation = useUpdateItem(nameId);
  const deleteItemMutation = useDeleteItem(nameId);
  const claimItemMutation = useClaimItem(nameId);
  const unclaimItemMutation = useUnclaimItem(nameId);
  const bulkImportMutation = useBulkImportItems(nameId);
  const reorderItemsMutation = useReorderItems(nameId);

  const [claimDialogItem, setClaimDialogItem] = useState(null);
  const [claimChoice, setClaimChoice] = useState("self");

  const [newItem, setNewItem] = useState("");
  const [newUrl, setNewUrl] = useState("");
  // store cents as digits string, e.g. "123" => $1.23
  const [newPriceDigits, setNewPriceDigits] = useState("");
  const [newThumbnailFile, setNewThumbnailFile] = useState(null);
  const [newThumbnailPreview, setNewThumbnailPreview] = useState(null);
  const [addPhotoError, setAddPhotoError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingUrl, setEditingUrl] = useState("");
  const [editingPriceDigits, setEditingPriceDigits] = useState("");
  const [editingThumbnail, setEditingThumbnail] = useState({ file: null, preview: null, removed: false });
  const [editPhotoError, setEditPhotoError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [purchasedLockOpen, setPurchasedLockOpen] = useState(false);
  // Mobile-only: which item's Edit/Delete overflow menu is open, if any.
  const [actionMenu, setActionMenu] = useState(null);

  const [addTab, setAddTab] = useState(0);
  const [importUrl, setImportUrl] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importSelected, setImportSelected] = useState({});
  const [importing, setImporting] = useState(false);

  const rankingHintKey = `wishlist-ranking-hint-dismissed-${userId}`;
  const [rankingHintDismissed, setRankingHintDismissed] = useState(() => {
    try {
      return localStorage.getItem(rankingHintKey) === "true";
    } catch {
      return false;
    }
  });

  const dismissRankingHint = () => {
    setRankingHintDismissed(true);
    try {
      localStorage.setItem(rankingHintKey, "true");
    } catch {
      // private browsing / storage disabled — just keep it dismissed for this render
    }
  };

  const sensors = useSensors(
    // Mouse gets a short drag distance; a hold delay would feel sluggish with a
    // pointer. Touch gets the hold-then-drag gesture, with a tolerance so a
    // vertical swipe during the hold window still scrolls the page.
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchAmazonItems = async () => {
    if (!importUrl.trim()) return;
    setImportLoading(true);
    setImportError("");
    setImportPreview(null);

    try {
      const existingTitles = items.map((i) => i.item_text);
      const taggedItems = await wishlistItemsService.previewAmazonImport(importUrl.trim(), existingTitles);
      const selected = {};
      taggedItems.forEach((item, idx) => { selected[idx] = !item.isDuplicate; });
      setImportPreview({ items: taggedItems });
      setImportSelected(selected);
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const executeImport = async () => {
    if (!importPreview) return;
    setImporting(true);

    // Imported items append below whatever is already ranked on the list.
    const baseSortOrder = items.length ? Math.max(...items.map((i) => i.sort_order ?? 0)) : 0;

    const toInsert = importPreview.items
      .filter((_, idx) => importSelected[idx])
      .map((item, offset) => ({
        name_id: nameId,
        item_text: item.title,
        url: `https://www.amazon.com/s?k=${encodeURIComponent(item.title)}`,
        price: item.price ? parseFloat(item.price) : null,
        thumbnail_url: item.thumbnailUrl || null,
        user_id: userId,
        sort_order: baseSortOrder + offset + 1,
      }));

    if (toInsert.length > 0) {
      try {
        await bulkImportMutation.mutateAsync(toInsert);
      } catch {
        // Error already logged in wishlistItemsService.
      }
    }

    setImportPreview(null);
    setImportUrl("");
    setImportSelected({});
    setAddTab(0);
    setImporting(false);
  };

  // ---- Thumbnail picker helpers ----
  const handleNewFileSelected = async (file) => {
    const { blob, error } = await validateAndResizeImage(file, 'WishlistItems');
    if (!blob) {
      setAddPhotoError(error);
      return;
    }
    if (newThumbnailPreview) URL.revokeObjectURL(newThumbnailPreview);
    setNewThumbnailFile(blob);
    setNewThumbnailPreview(URL.createObjectURL(blob));
    setAddPhotoError("");
  };

  const clearNewThumbnail = () => {
    if (newThumbnailPreview) URL.revokeObjectURL(newThumbnailPreview);
    setNewThumbnailFile(null);
    setNewThumbnailPreview(null);
    setAddPhotoError("");
  };

  const handleEditFileSelected = async (file) => {
    const { blob, error } = await validateAndResizeImage(file, 'WishlistItems');
    if (!blob) {
      setEditPhotoError(error);
      return;
    }
    setEditingThumbnail((prev) => {
      if (prev.file && prev.preview) URL.revokeObjectURL(prev.preview);
      return { file: blob, preview: URL.createObjectURL(blob), removed: false };
    });
    setEditPhotoError("");
  };

  const clearEditThumbnail = () => {
    setEditingThumbnail((prev) => {
      if (prev.file && prev.preview) URL.revokeObjectURL(prev.preview);
      return { file: null, preview: null, removed: true };
    });
    setEditPhotoError("");
  };

  const addItem = async () => {
    if (!newItem.trim()) return;

    const price = digitsToNumber(newPriceDigits);
    let thumbnailUrl = null;

    if (newThumbnailFile) {
      try {
        thumbnailUrl = await wishlistItemsService.uploadThumbnail(newThumbnailFile, nameId, userId);
      } catch {
        setAddPhotoError("Photo upload failed. Try again, or remove the photo to add the item without one.");
        return;
      }
    }

    // New ideas start at the bottom of the ranking; the owner drags them up.
    const nextSortOrder = items.length ? Math.max(...items.map((i) => i.sort_order ?? 0)) + 1 : 1;

    try {
      await addItemMutation.mutateAsync({
        nameId,
        userId,
        itemText: newItem.trim(),
        url: newUrl.trim() || null,
        price,
        thumbnailUrl,
        sortOrder: nextSortOrder,
      });
      setNewItem("");
      setNewUrl("");
      setNewPriceDigits("");
      clearNewThumbnail();
    } catch {
      // Error already logged in wishlistItemsService; clean up the orphaned upload, if any.
      if (thumbnailUrl) await wishlistItemsService.deleteThumbnail(thumbnailUrl);
    }
  };

  const handleClaim = async (item, claimForChildName = null) => {
    try {
      await claimItemMutation.mutateAsync({ itemId: item.id, userId, claimedForChildName: claimForChildName });
    } catch {
      // Error already logged in wishlistItemsService.
    }
  };

  const handleUnclaim = async (item) => {
    try {
      await unclaimItemMutation.mutateAsync({ itemId: item.id, currentClaimantId: item.claimed_by, userId });
    } catch {
      // Error already logged in wishlistItemsService, or the claimant guard rejected this locally.
    }
  };

  const toggleClaim = async (item, claimForChildName = null) => {
    if (item.claimed_by) {
      await handleUnclaim(item);
    } else {
      await handleClaim(item, claimForChildName);
    }
  };

  const openClaimDialog = (item) => {
    setClaimChoice("self");
    setClaimDialogItem(item);
  };

  const confirmClaimDialog = async () => {
    if (!claimDialogItem) return;
    const childName = claimChoice !== "self" ? claimChoice : null;
    await toggleClaim(claimDialogItem, childName);
    setClaimDialogItem(null);
  };

  const openActionMenu = (event, item) => setActionMenu({ itemId: item.id, anchorEl: event.currentTarget });
  const closeActionMenu = () => setActionMenu(null);

  const handleStartEdit = (item) => {
    if (item?.purchased_at) {
      setPurchasedLockOpen(true);
      return;
    }

    setEditingId(item.id);
    setEditingText(item.item_text);
    setEditingUrl(item.url || "");
    setEditingPriceDigits(numberToDigits(item.price));
    setEditingThumbnail({ file: null, preview: item.thumbnail_url || null, removed: false });
    setEditPhotoError("");
  };

  const saveEdit = async (id) => {
    const existing = items.find((x) => x.id === id);
    if (existing?.purchased_at) {
      setPurchasedLockOpen(true);
      return;
    }

    const price = digitsToNumber(editingPriceDigits);
    let thumbnailUrl = existing.thumbnail_url;
    let newlyUploadedUrl = null;

    if (editingThumbnail.file) {
      try {
        thumbnailUrl = await wishlistItemsService.uploadThumbnail(editingThumbnail.file, nameId, userId, { itemId: id });
        newlyUploadedUrl = thumbnailUrl;
      } catch {
        setEditPhotoError("Photo upload failed. Try again, or remove the photo to save without one.");
        return;
      }
    } else if (editingThumbnail.removed) {
      thumbnailUrl = null;
    }

    try {
      await updateItemMutation.mutateAsync({ id, itemText: editingText, url: editingUrl || null, price, thumbnailUrl });
      setEditingId(null);
      if (thumbnailUrl !== existing.thumbnail_url) {
        wishlistItemsService.deleteThumbnail(existing.thumbnail_url);
      }
    } catch {
      // Error already logged in wishlistItemsService; clean up the orphaned upload, if any.
      if (newlyUploadedUrl) await wishlistItemsService.deleteThumbnail(newlyUploadedUrl);
    }
  };

  const handleStartDelete = (item) => {
    if (item?.purchased_at) {
      setPurchasedLockOpen(true);
      return;
    }
    setDeleteTarget(item);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    if (deleteTarget?.purchased_at) {
      setDeleteTarget(null);
      setPurchasedLockOpen(true);
      return;
    }

    const target = deleteTarget;
    try {
      await deleteItemMutation.mutateAsync({ id: target.id });
      setDeleteTarget(null);
      wishlistItemsService.deleteThumbnail(target.thumbnail_url);
    } catch {
      // Error already logged in wishlistItemsService; leave the dialog open so the user can retry.
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;

    const payload = computeReorderPayload(items, active.id, over.id);
    if (!payload) return;

    const { reordered, updates } = payload;
    // Optimistic: reflect the new order instantly rather than waiting on the
    // network round-trip, then persist and let onSuccess reconcile with the
    // server (and broadcast to other viewers).
    queryClient.setQueryData(["wishlistItems", nameId], reordered);

    // One statement instead of one UPDATE per moved row: dragging to the top of a
    // long list was N round-trips, and a partial failure could leave some rows
    // committed and others not. The RPC applies them in a single transaction.
    reorderPendingRef.current = true;
    try {
      await reorderItemsMutation.mutateAsync({ nameId, updates, order: reordered.map((i) => i.id) });
    } catch {
      // Error already logged in wishlistItemsService; fall back to server truth.
      queryClient.invalidateQueries({ queryKey: ["wishlistItems", nameId] });
    } finally {
      reorderPendingRef.current = false;
    }
  };

  return (
    <Paper elevation={2} className="page-card" sx={{ p: { xs: 1.5, sm: 3 }, mb: 4 }}>
      <Stack spacing={3}>
        {isOwner && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Tabs
              value={addTab}
              onChange={(_, v) => setAddTab(v)}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Add a gift idea" />
              <Tab label="Import from Amazon" />
            </Tabs>

            {addTab === 0 && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch" sx={{ minWidth: 0 }}>
                <ThumbnailPicker
                  preview={newThumbnailPreview}
                  onFileSelected={handleNewFileSelected}
                  onClear={clearNewThumbnail}
                  error={addPhotoError}
                />
                <TextField
                  label="Item"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Optional URL"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Price"
                  value={digitsToDisplay(newPriceDigits)}
                  onChange={handleDigitsChange(setNewPriceDigits)}
                  inputMode="numeric"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <Tooltip title={isWishlistLocked ? LOCKED_TOOLTIP : ""}>
                  <span>
                    <Button variant="contained" onClick={addItem} disabled={isWishlistLocked} sx={{ minWidth: 120 }}>
                      Add
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            )}

            {addTab === 1 && (
              <Stack spacing={2}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Paste your Amazon wishlist print view URL (from Amazon's "Print List" option).
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
                  <TextField
                    label="Amazon Print View URL"
                    value={importUrl}
                    onChange={(e) => {
                      setImportUrl(e.target.value);
                      setImportError("");
                    }}
                    fullWidth
                    placeholder="https://www.amazon.com/hz/wishlist/printview/..."
                    error={!!importError}
                    helperText={importError}
                  />
                  <Tooltip title={isWishlistLocked ? LOCKED_TOOLTIP : ""}>
                    <span>
                      <Button
                        variant="contained"
                        onClick={fetchAmazonItems}
                        disabled={isWishlistLocked || importLoading || !importUrl.trim()}
                        sx={{ minWidth: 140, height: 56, flexShrink: 0 }}
                        startIcon={importLoading ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {importLoading ? "Fetching..." : "Fetch Items"}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            )}
          </Stack>
        )}

        {isOwner && items.length > 0 && !rankingHintDismissed && (
          <Alert severity="info" onClose={dismissRankingHint}>
            Drag items by the handle to put them in order — your most-wanted gifts first. Your
            group can see the order, and it saves automatically.
          </Alert>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <List sx={{ display: "grid", gap: { xs: 2, sm: 2.5 } }}>
              {items.map((item, index) => {
                const editing = editingId === item.id;
                const isPurchased = !!item.purchased_at;
                const isClaimed = !!item.claimed_by;
                const claimedByMe = item.claimed_by === userId;
                // A surprise item merged in from someone else's Shopping List — name_id is
                // null (it isn't really "on" this list), it just names this name as its
                // surprise_recipient_name_id. Read-only here; edit/delete stays ClaimedItems-only.
                const isSurprise = item.name_id === null;

                return (
                  <SortableItemCard
                    key={item.id}
                    id={item.id}
                    rank={index + 1}
                    disabled={!isOwner || isWishlistLocked}
                    showHandle={isOwner && !editing}
                    locked={isWishlistLocked}
                  >
                    {editing ? (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                        <ThumbnailPicker
                          preview={editingThumbnail.preview}
                          onFileSelected={handleEditFileSelected}
                          onClear={clearEditThumbnail}
                          error={editPhotoError}
                        />
                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                          <TextField
                            label="Item"
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            fullWidth
                          />
                          <TextField
                            label="URL"
                            value={editingUrl}
                            onChange={(e) => setEditingUrl(e.target.value)}
                            fullWidth
                          />
                          <TextField
                            label="Price"
                            value={digitsToDisplay(editingPriceDigits)}
                            onChange={handleDigitsChange(setEditingPriceDigits)}
                            inputMode="numeric"
                            fullWidth
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                          />
                          <Stack direction="row" spacing={2}>
                            <Button
                              variant="contained"
                              onClick={() => saveEdit(item.id)}
                            >
                              Save
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                if (editingThumbnail.file && editingThumbnail.preview) {
                                  URL.revokeObjectURL(editingThumbnail.preview);
                                }
                                setEditingId(null);
                              }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    ) : isMobile ? (
                      <Box sx={{ width: "100%" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ItemThumbnail src={item.thumbnail_url} size={THUMBNAIL_SIZE} hideIfEmpty />
                          <Box sx={{ flex: 1 }} />
                          {isOwner && (
                            <>
                              <Tooltip title={isWishlistLocked ? LOCKED_TOOLTIP : ""}>
                                <span>
                                  <IconButton
                                    aria-label="more actions"
                                    onClick={(e) => openActionMenu(e, item)}
                                    disabled={isWishlistLocked}
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Menu
                                anchorEl={actionMenu?.itemId === item.id ? actionMenu.anchorEl : null}
                                open={actionMenu?.itemId === item.id}
                                onClose={closeActionMenu}
                              >
                                <MenuItem
                                  aria-label="edit"
                                  onClick={() => {
                                    closeActionMenu();
                                    handleStartEdit(item);
                                  }}
                                >
                                  Edit
                                </MenuItem>
                                <MenuItem
                                  aria-label="delete"
                                  onClick={() => {
                                    closeActionMenu();
                                    handleStartDelete(item);
                                  }}
                                  sx={{ color: "error.main" }}
                                >
                                  Delete
                                </MenuItem>
                              </Menu>
                            </>
                          )}
                        </Stack>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mt: 1.5,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {item.url ? (
                            <Link href={item.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#1976d2' }}>
                              {item.item_text}
                            </Link>
                          ) : item.item_text}
                        </Typography>
                        {isSurprise ? (
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                            <Chip label="Surprise" size="small" color="secondary" />
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Purchased by {item.users?.display_name || "someone in your group"}
                            </Typography>
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 700 }}>
                              ${Number(item.price || 0).toFixed(2)}
                            </Typography>
                            {!hideClaimButton && isClaimed && (
                              <Typography sx={{ color: "success.main", fontWeight: 600 }}>
                                · Claimed{item.claimed_for_child_name
                                  ? ` for ${item.claimed_for_child_name}`
                                  : item.users?.display_name ? ` by ${item.users.display_name}` : ""}
                              </Typography>
                            )}
                            {!hideClaimButton && isPurchased && (
                              <Typography sx={{ color: "warning.main", fontWeight: 600 }}>
                                · Purchased
                              </Typography>
                            )}
                          </Stack>
                        )}
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                          Added {new Date(item.created_at).toLocaleDateString()}
                        </Typography>
                        {!hideClaimButton && !isSurprise && (!isClaimed || claimedByMe) && (
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                            <Button
                              variant="contained"
                              onClick={() =>
                                !isClaimed && showChildSelector
                                  ? openClaimDialog(item)
                                  : toggleClaim(item)
                              }
                            >
                              {isClaimed ? "Unclaim" : "Claim"}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="flex-start">
                        <ItemThumbnail src={item.thumbnail_url} size={THUMBNAIL_SIZE} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {item.url ? (
                              <Link href={item.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#1976d2' }}>
                                {item.item_text}
                              </Link>
                            ) : item.item_text}
                          </Typography>
                          {isSurprise ? (
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                              <Chip label="Surprise" size="small" color="secondary" />
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                Purchased by {item.users?.display_name || "someone in your group"}
                              </Typography>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                              <Typography sx={{ fontWeight: 700 }}>
                                ${Number(item.price || 0).toFixed(2)}
                              </Typography>
                              {!hideClaimButton && isClaimed && (
                                <Typography sx={{ color: "success.main", fontWeight: 600 }}>
                                  · Claimed{item.claimed_for_child_name
                                    ? ` for ${item.claimed_for_child_name}`
                                    : item.users?.display_name ? ` by ${item.users.display_name}` : ""}
                                </Typography>
                              )}
                              {!hideClaimButton && isPurchased && (
                                <Typography sx={{ color: "warning.main", fontWeight: 600 }}>
                                  · Purchased
                                </Typography>
                              )}
                            </Stack>
                          )}
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                            Added {new Date(item.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexShrink: 0, alignItems: "center" }}>
                          {!hideClaimButton && !isSurprise && (!isClaimed || claimedByMe) && (
                            <Button
                              variant="contained"
                              onClick={() =>
                                !isClaimed && showChildSelector
                                  ? openClaimDialog(item)
                                  : toggleClaim(item)
                              }
                            >
                              {isClaimed ? "Unclaim" : "Claim"}
                            </Button>
                          )}
                          {isOwner && (
                            <Tooltip title={isWishlistLocked ? LOCKED_TOOLTIP : ""}>
                              <span>
                                <IconButton
                                  aria-label="edit"
                                  onClick={() => handleStartEdit(item)}
                                  disabled={isWishlistLocked}
                                >
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {isOwner && (
                            <Tooltip title={isWishlistLocked ? LOCKED_TOOLTIP : ""}>
                              <span>
                                <IconButton
                                  aria-label="delete"
                                  color="error"
                                  onClick={() => handleStartDelete(item)}
                                  disabled={isWishlistLocked}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    )}
                  </SortableItemCard>
                );
              })}
            </List>
          </SortableContext>
        </DndContext>
      </Stack>

      <Dialog open={purchasedLockOpen} onClose={() => setPurchasedLockOpen(false)}>
        <DialogTitle>Locked Item</DialogTitle>
        <DialogContent>
          Purchased items cannot be edited or deleted.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchasedLockOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(claimDialogItem)} onClose={() => setClaimDialogItem(null)}>
        <DialogTitle>Claim this gift as?</DialogTitle>
        <DialogContent>
          <FormControl>
            <FormLabel sx={{ mb: 1 }}>{claimDialogItem?.item_text}</FormLabel>
            <RadioGroup
              value={claimChoice}
              onChange={(e) => setClaimChoice(e.target.value)}
            >
              <FormControlLabel value="self" control={<Radio />} label="Myself" />
              {childLists.map((c) => (
                <FormControlLabel key={c.id} value={c.name} control={<Radio />} label={c.name} />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimDialogItem(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmClaimDialog}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Gift</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this gift idea?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!importPreview}
        onClose={() => setImportPreview(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import from Amazon</DialogTitle>
        <DialogContent>
          {importPreview && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                {importPreview.items.length} item{importPreview.items.length !== 1 ? "s" : ""} found.
                {importPreview.items.some((i) => i.isDuplicate) &&
                  " Possible duplicates are unchecked — check them to add anyway."}
              </Typography>
              {importPreview.items.map((item, idx) => (
                <Stack
                  key={idx}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: item.isDuplicate ? "action.hover" : "transparent",
                  }}
                >
                  <Checkbox
                    checked={!!importSelected[idx]}
                    onChange={(e) =>
                      setImportSelected((prev) => ({ ...prev, [idx]: e.target.checked }))
                    }
                  />
                  {item.thumbnailUrl && (
                    <Box
                      component="img"
                      src={item.thumbnailUrl}
                      alt=""
                      sx={{
                        width: 48,
                        height: 48,
                        objectFit: "contain",
                        flexShrink: 0,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {item.price ? `$${Number(item.price).toFixed(2)}` : "No price listed"}
                      {item.isDuplicate && " · Possible duplicate"}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportPreview(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={executeImport}
            disabled={importing || !Object.values(importSelected).some(Boolean)}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {importing
              ? "Adding..."
              : `Add ${Object.values(importSelected).filter(Boolean).length} item${
                  Object.values(importSelected).filter(Boolean).length !== 1 ? "s" : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
